import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Col
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  dispositionProcessAssign,
  processDispositionAssignedListGet,
  processDispositionNotAssignedListGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import FormInput from '../../../components/form/FormInput';

const AssignDialableStatusModal = ({ isOpen, onCloseClick, process_id = null }) => {
  const dispatch = useDispatch();
  const [availableDisposition, setAvailableDisposition] = useState([]);
  const [assignedDisposition, setAssignedDisposition] = useState([]);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchAssigned, setSearchAssigned] = useState('');
  const [filteredAvailableDisposition, setFilteredAvailableDisposition] = useState([]);
  const [filteredAssignedDisposition, setFilteredAssignedDisposition] = useState([]);

  useEffect(() => {
    if (process_id && isOpen) {
      fetchProcessDispositionAssignedList();
      fetchProcessDispositionNotAssignedList();
    }
  }, [process_id, isOpen]);

  const fetchProcessDispositionNotAssignedList = async () => {
    try {
      const response = await dispatch(
        processDispositionNotAssignedListGet({ process_id })
      ).unwrap();
      if (response) {
        setAvailableDisposition(response?.data || []);
        setFilteredAvailableDisposition(response?.data || []);
      }
    } catch (error) {
      console.log('Error fetching available Disposition:', error);
    }
  };

  const fetchProcessDispositionAssignedList = async () => {
    try {
      const response = await dispatch(processDispositionAssignedListGet({ process_id })).unwrap();
      if (response) {
        setAssignedDisposition(response?.data || []);
        setFilteredAssignedDisposition(response?.data || []);
      }
    } catch (error) {
      console.log('Error fetching assigned Disposition:', error);
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    const updatedAvailableDisposition = [...availableDisposition];
    const updatedAssignedDisposition = [...assignedDisposition];

    if (source.droppableId === 'available' && destination.droppableId === 'assigned') {
      const movedItem = filteredAvailableDisposition[source.index];
      updatedAvailableDisposition.splice(updatedAvailableDisposition.indexOf(movedItem), 1);
      updatedAssignedDisposition.splice(destination.index, 0, movedItem);
    }

    if (source.droppableId === 'assigned' && destination.droppableId === 'available') {
      const movedItem = filteredAssignedDisposition[source.index];
      updatedAssignedDisposition.splice(updatedAssignedDisposition.indexOf(movedItem), 1);
      updatedAvailableDisposition.splice(destination.index, 0, movedItem);
    }

    setAvailableDisposition(updatedAvailableDisposition);
    setAssignedDisposition(updatedAssignedDisposition);
    setFilteredAvailableDisposition(
      updatedAvailableDisposition.filter((item) =>
        item.disposition_name.toLowerCase().includes(searchAvailable.toLowerCase())
      )
    );
    setFilteredAssignedDisposition(
      updatedAssignedDisposition.filter((item) =>
        item.disposition_name.toLowerCase().includes(searchAssigned.toLowerCase())
      )
    );
  };

  const onSubmit = async () => {
    try {
      const queue_ids = assignedDisposition.map((x) => x.disposition_name);
      const payload = {
        disposition_name: queue_ids,
        process_id: process_id
      };
      const response = await dispatch(dispositionProcessAssign(payload)).unwrap();
      toast.success(response.message);
      onCloseClick();
      setAvailableDisposition([]);
      setAssignedDisposition([]);
    } catch (error) {
      console.log('Error during queue assignment:', error);
    }
  };

  useEffect(() => {
    setFilteredAvailableDisposition(
      availableDisposition.filter((item) =>
        item.disposition_name.toLowerCase().includes(searchAvailable.toLowerCase())
      )
    );
  }, [searchAvailable, availableDisposition]);

  useEffect(() => {
    setFilteredAssignedDisposition(
      assignedDisposition.filter((item) =>
        item.disposition_name.toLowerCase().includes(searchAssigned.toLowerCase())
      )
    );
  }, [searchAssigned, assignedDisposition]);

  useEffect(() => {
    if (!isOpen) {
      setAvailableDisposition([]);
      setAssignedDisposition([]);
      setFilteredAvailableDisposition([]);
      setFilteredAssignedDisposition([]);
      setSearchAvailable('');
      setSearchAssigned('');
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Assign Disposition</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <Row className="py-6">
          <DragDropContext onDragEnd={onDragEnd}>
            <Row className="py-6">
              <Col xs="6" md="6" sm="6" lg="6" className="mr-10">
                <Card>
                  <CardHeader className="bg-info py-2 text-dark text-center text-white">
                    <div>Available Disposition</div>
                  </CardHeader>
                  <CardBody
                    className="bg-dark-950"
                    style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', height: 'auto' }}>
                    <FormInput
                      placeholder="Search available Disposition"
                      value={searchAvailable}
                      onChange={(e) => setSearchAvailable(e.target.value)}
                      className="mb-3"
                    />
                    <Droppable droppableId="available">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={
                            filteredAvailableDisposition.length === 0
                              ? 'p-2 border mb-3 bg-white rounded-2 text-primary'
                              : ''
                          }>
                          {filteredAvailableDisposition.length > 0 ? (
                            filteredAvailableDisposition.map((item, index) => (
                              <Draggable
                                key={item.disposition_name}
                                draggableId={String(item.disposition_name)}
                                index={index}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="p-2 border mb-3 bg-white rounded-2 text-primary">
                                    {item.disposition_name || '-'}
                                  </div>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <div>No available Disposition</div>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </CardBody>
                </Card>
              </Col>

              <Col xs="6" md="6" sm="6" lg="6">
                <Card>
                  <CardHeader className="bg-primary py-2 text-dark text-center text-white">
                    <div>Assigned Disposition</div>
                  </CardHeader>
                  <CardBody
                    className="bg-dark-950"
                    style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', height: 'auto' }}>
                    <FormInput
                      placeholder="Search assigned Disposition"
                      value={searchAssigned}
                      onChange={(e) => setSearchAssigned(e.target.value)}
                      className="mb-3"
                    />
                    <Droppable droppableId="assigned">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={
                            filteredAssignedDisposition.length === 0
                              ? 'p-2 border mb-3 bg-white rounded-2 text-primary'
                              : ''
                          }>
                          {filteredAssignedDisposition.length > 0 ? (
                            filteredAssignedDisposition.map((item, index) => (
                              <Draggable
                                key={item.disposition_name}
                                draggableId={String(item.disposition_name)}
                                index={index}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="p-2 border mb-3 bg-white rounded-2 text-primary">
                                    {item.disposition_name || '-'}
                                  </div>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <div>No assigned Disposition</div>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </DragDropContext>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setAvailableDisposition([]);
            setAssignedDisposition([]);
            setFilteredAvailableDisposition([]);
            setFilteredAssignedDisposition([]);
            setSearchAvailable('');
            setSearchAssigned('');
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button
          form="Disposition"
          type="submit"
          className="px-5 bg-primary border-primary"
          onClick={onSubmit}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AssignDialableStatusModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  process_id: PropTypes.any
};

export default AssignDialableStatusModal;
